import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-turnstile',
  templateUrl: './turnstile.component.html',
  styleUrl: './turnstile.component.scss',
})
export class TurnstileComponent implements OnDestroy, AfterViewInit {
  @Output() tokenReceived = new EventEmitter<string | null>();
  @Input() onRenew: Subject<void> | null = null;

  TURNSTILE_SITE_KEY = '0x4AAAAAABmUALOGbOjJ3EQz';

  errorMessage: string | null = null;

  private static readonly SCRIPT_TIMEOUT_MS = 10000;
  private static turnstileReadyPromise: Promise<void> | null = null;

  private widgetId: string | null = null;
  private renewSub?: Subscription;
  private readonly isBrowser: boolean;

  constructor(
    private elRef: ElementRef,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    if (this.onRenew) {
      this.renewSub = this.onRenew.subscribe(() => this.resetTurnstile());
    }

    this.ensureWidget();
  }

  ngOnDestroy(): void {
    this.renewSub?.unsubscribe();

    // The login form hides this component once the password step is done, so
    // it is now destroyed and recreated during a single visit. Cloudflare
    // keeps its own registry of rendered widgets; without removing ours, each
    // round leaves an orphaned widget (and its timers) behind.
    const turnstile = this.getTurnstileGlobal();
    if (turnstile && this.widgetId) {
      try {
        turnstile.remove(this.widgetId);
      } catch (error) {
        console.error('Failed to remove Turnstile widget', error);
      }
      this.widgetId = null;
    }
  }

  private ensureWidget(): void {
    this.waitForTurnstile()
      .then(() => this.renderWidget())
      .catch(() => {
        this.handleError(
          'Unable to load the verification widget. Please reload the page or contact info@alphabees.de.'
        );
      });
  }

  private waitForTurnstile(): Promise<void> {
    if (!this.isBrowser) {
      return Promise.resolve();
    }

    if (this.getTurnstileGlobal()) {
      return Promise.resolve();
    }

    if (!TurnstileComponent.turnstileReadyPromise) {
      TurnstileComponent.turnstileReadyPromise = new Promise(
        (resolve, reject) => {
          const start = Date.now();

          const check = () => {
            if (this.getTurnstileGlobal()) {
              resolve();
              return;
            }

            if (Date.now() - start > TurnstileComponent.SCRIPT_TIMEOUT_MS) {
              TurnstileComponent.turnstileReadyPromise = null;
              reject(new Error('Turnstile script timed out.'));
              return;
            }

            window.setTimeout(check, 100);
          };

          check();
        }
      );
    }

    return TurnstileComponent.turnstileReadyPromise!;
  }

  private renderWidget(): void {
    if (this.widgetId) {
      return;
    }

    const turnstile = this.getTurnstileGlobal();

    if (!turnstile) {
      this.handleError(
        'Turnstile failed to initialize. Please reload the page or contact info@alphabees.de.'
      );
      return;
    }

    this.widgetId = turnstile.render(
      this.elRef.nativeElement.querySelector('#cf-turnstile'),
      {
        sitekey: this.TURNSTILE_SITE_KEY,
        theme: 'light',
        size: 'normal',
        callback: (token: string) => {
          if (!token) {
            this.handleError(
              'Capture verification failed. Please retry or contact info@alphabees.de.'
            );
            return;
          }
          this.errorMessage = null;
          this.tokenReceived.emit(token);
        },
        'expired-callback': () => {
          this.handleError('Turnstile expired. Please retry the verification.');
          this.resetTurnstile();
        },
        'timeout-callback': () => {
          this.handleError('Turnstile verification timed out. Please retry.');
          this.resetTurnstile();
        },
        'error-callback': () => {
          this.handleError(
            'An error occurred during verification. Please retry.'
          );
        },
        'unsupported-callback': () => {
          this.handleError(
            'Turnstile is unsupported in this browser. Please try a different browser or contact info@alphabees.de.'
          );
        },
      }
    );
  }

  private resetTurnstile(): void {
    this.errorMessage = null;
    this.tokenReceived.emit(null);

    const turnstile = this.getTurnstileGlobal();

    if (!turnstile || !this.widgetId) {
      return;
    }

    try {
      turnstile.reset(this.widgetId);
    } catch (error) {
      console.error('Failed to reset Turnstile widget', error);
      this.handleError(
        'Unable to refresh the verification widget. Please reload the page.'
      );
    }
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    this.tokenReceived.emit(null);
  }

  private getTurnstileGlobal(): any | undefined {
    if (!this.isBrowser) {
      return undefined;
    }

    return (window as any)?.turnstile;
  }
}
